import React, { KeyboardEvent } from "react";
import { connect } from "react-redux";
import { Box, TextInput, Button, CheckBox, Text, ResponsiveContext, Heading, Layer, Select, Anchor, Grid, Spinner, Image } from "grommet";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { RouteComponentProps, withRouter } from "react-router";
import { ApplicationState } from '../store';
import { performSearch, searchSlice, defaultSortOrder, SearchRequest, initialState } from "../store/Search";
import { siteSlice } from '../store/Site';
import { CategoryItem } from '../models/CategoryItem';
import { TaxonomyItem } from '../models/TaxonomyItem';
import { VideoInfo } from '../models/VideoInfo';
import { Search as SearchIcon } from "grommet-icons";
import TreeListView from "../components/TreeListView";
import InfoCard from "../components/InfoCard";
import { setupMetrics, getMappedLang } from "../app/utilities";
import { Helmet } from "react-helmet";
import queryString from "query-string";
import _ from "lodash";

const mapDispatchToProps = {
    performSearch,
    ...searchSlice.actions,
    ...siteSlice.actions
};

interface ISearchProps {
    id: string;
    max?: string;
    category?: string;
}

interface ISearchState {
    showSearch: boolean;
    scopeText: string;
    collapsed: boolean;
    taxonomy: any;
}

type SearchProps = ISearchProps &
    ApplicationState // ... state we've requested from the Redux store
    & typeof mapDispatchToProps
    & RouteComponentProps<any>; // ... plus incoming routing parameters


class Search extends React.PureComponent<SearchProps, ISearchState> {
    constructor(props: SearchProps) {
        super(props);

        let currentState = this.props.location.state as SearchRequest;
        let currentQuery = queryString.parse(this.props.location.search);
        let scopeText = "";

        if (currentQuery.lang) {
            let lang = `${currentQuery.lang}`;

            if (lang) {
                let mappedLang = getMappedLang(lang);
                let urlLang = mappedLang?.value ?? lang;
                let langItems = urlLang.indexOf('-') !== -1
                    ? urlLang.split("-")
                    : ["", ""];
                let country = langItems[1].toUpperCase();
                let language = urlLang === 'Other' || !mappedLang
                    ? 'Other'
                    : `${langItems[0]}-${country}`;

                this.props.setLanguage({
                    text: "Url Language",
                    value: language,
                });
            }
        }
        if (currentQuery.cc && currentQuery.cc === 'off') {
            this.props.setIncludeCaptions(false);
        }
        if (currentState === undefined) {
            if (this.props.match.params.category) {
                let category = this.props.match.params.category.replace(/^\w/, (c: string) => c.toUpperCase());

                this.props.setCategory(category);
                this.props.setScope("CATEGORY");
                scopeText = category;
            }
        } else if (currentState.category) {
            let category = currentState.category.replace(/^\w/, (c) =>
                c.toUpperCase()
            );

            this.props.setCategory(category);
            this.props.setScope("CATEGORY");
            this.props.setRequest(currentState);
            scopeText = category;
        }
        this.state = {
            showSearch: false,
            scopeText: scopeText,
            collapsed: true,
            taxonomy: [],
        };
        setTimeout(() => {
            this.performSearch();
        }, 0);
    }

    public componentDidMount() {
        setupMetrics("Videos");
    }

    toggleNavbar = () => {
        this.setState({ collapsed: !this.state.collapsed });
    };

    updateTaxonomy = (event: any, item: TaxonomyItem) => {
        let taxonomyJson = JSON.stringify(this.props.search.request.taxonomy);
        let taxonomy = JSON.parse(taxonomyJson);
        let parentItem = _.find(taxonomy, (t) => {
            return t.text === (item.parent ?? item.text);
        });
        let foundItem = item.parent
            ? _.find(parentItem.children, (t) => { return t.text === item.text; })
            : parentItem;

        if (foundItem) {
            foundItem.isSelected = event.target.checked
                ? event.target.checked
                : !foundItem.isSelected;
            this.props.setTaxonomy(taxonomy);
        }
        if (foundItem.parent) {
            setTimeout(() => {
                this.performSearch();
            }, 0);
        }
    };

    resetSearch = () => {
        var location = Object.assign({}, this.props.history.location);
        var request = Object.assign({}, initialState.request);
        let taxonomyJson = JSON.stringify(this.props.search.results.taxonomy);
        let taxonomy = JSON.parse(taxonomyJson);

        request.taxonomy = taxonomy.map((item: TaxonomyItem) => {
            item.isSelected = false;
            item.children = item.children.map((child: TaxonomyItem) => {
                child.isSelected = false;

                return child;
            });
            return item;
        });
        this.props.setRequest(request);
        this.props.history.push({
            pathname: location.pathname,
            search: "lang=",
            state: request,
        });
        setTimeout(() => {
            this.performSearch();
        }, 0);
    };

    renderSearch = () => {
        return (
            <Box fill pad="small" gap="medium">
                <Box gap="xsmall" fill="horizontal">
                    <Box direction="row" gap="xsmall" pad={{ bottom: "small" }}>
                        <TextInput size="small" placeholder="Search videos ..." value={this.props.search.request.keyword}
                            onChange={(event) => this.onKeywordChange(event.target.value)} />
                        <Button onClick={() => { this.onSearchClicked(); }} icon={<SearchIcon />} />
                    </Box>
                    <Box fill="horizontal">
                        <Anchor size="xsmall" onClick={() => { this.resetSearch(); }} label="Reset Search" alignSelf="end" />
                    </Box>
                </Box>
                <Box gap="xsmall" fill="horizontal" height={{ min: "xsmall" }}>
                    <Text weight="bold" size="xsmall">Language</Text>
                    <Select options={this.props.search.results.languages} value={this.props.search.request.language} size="small"
                        valueKey="value" labelKey="text" onChange={({ value }) => { this.onLanguageChange(value); }} dropHeight="medium" />
                    <div className="check-box">
                        <CheckBox checked={this.props.search.request.includeCaptions} label="include captions and alternate audio tracks"
                            onChange={(event) => this.onIncludeCaptionChange(event.target.checked)} />
                    </div>
                </Box>
                <Box gap="xsmall" fill>
                    <Text weight="bold" size="xsmall">
                        Category
                    </Text>
                    <Box gap="xsmall" height={{ min: "medium", max: "large" }} overflow="auto" >
                        <TreeListView data={this.props.search.request.taxonomy} callback={this.updateTaxonomy} />
                    </Box>
                </Box>
            </Box>
        );
    };

    renderResults = (size: string) => {
        let videos = this.props.search.results.videos;
        let pageSize = this.props.search.request.pageSize;
        let videoStart = (this.props.search.request.currentPage - 1) * pageSize + 1;
        let videoCount = videos.length;
        let videoEnd =
            videoCount < pageSize
                ? videoStart + videoCount - 1
                : videoStart + pageSize - 1;
        let pagination = this.renderPagination(size);

        return (
            <Box fill align="center" justify="center">
                {videoCount !== 0 ? (
                    <Box fill gap="none" pad={{ left: "small" }} align="center">
                        {size === "small" || size === "xsmall" ? (
                            <Box fill="horizontal" direction="row" gap="medium" justify="between" >
                                <Box alignSelf="start">
                                    <Button
                                        onClick={() => this.toggleShowSearch()}
                                        icon={<SearchIcon size="medium" />}
                                    />
                                </Box>
                                <Box align="baseline" margin={{ top: "medium" }}>
                                    {pagination}
                                </Box>
                                <Box alignSelf="end">
                                    <Text size="small" color="brand">
                                        {videoStart} - {videoEnd} of{" "}
                                        {this.props.search.results.totalVideos}
                                    </Text>
                                </Box>
                            </Box>
                        ) : (
                            <Box fill="horizontal" direction="row" gap="medium" justify="between" >
                                <Box
                                    direction="row"
                                    alignSelf="start"
                                    width={{ min: "small" }}
                                    align="baseline"
                                    margin={{ top: "small" }}
                                >
                                    {pagination}
                                </Box>
                                {size !== "medium_small" && size !== "medium" && (
                                    <Box
                                        gap="xsmall"
                                        direction="row-responsive"
                                        align="center"
                                    >
                                        <Text
                                            weight="bold"
                                            size="xsmall"
                                            color="brand"
                                        >
                                            Sort By
                                        </Text>
                                        <Select
                                            options={
                                                this.props.search.results.sortOrders
                                            }
                                            value={this.props.search.request.sortBy}
                                            size="small"
                                            valueKey="value"
                                            labelKey="text"
                                            onChange={({ value }) => {
                                                this.onSorOrderChange(value);
                                            }}
                                        />
                                    </Box>
                                )}
                                <Box alignSelf="end" align="end">
                                    <Text size="small" color="brand">
                                        {videoStart} - {videoEnd} of{" "}
                                        {this.props.search.results.totalVideos}
                                    </Text>
                                </Box>
                            </Box>
                        )}
                        <Grid
                            gap={{ row: "small", column: "small" }}
                            rows="auto"
                            columns="150px"
                            fill
                            pad="small"
                        >
                            {videos.map((video) => {
                                return (
                                    <InfoCard
                                        key={`video-${video.id}`}
                                        data={video}
                                        onClick={() => {
                                            this.gotoVideo(video);
                                        }}
                                        {...this.props}
                                    />
                                );
                            })}
                        </Grid>
                        <Box
                            fill="horizontal"
                            direction="row"
                            justify="between"
                            align="baseline"
                            pad={{ bottom: "small" }}
                        >
                            <Box
                                direction="row"
                                alignSelf="start"
                                width={{ min: "small" }}
                                align="baseline"
                                margin={{ top: "small" }}
                            >
                                {pagination}
                            </Box>
                            <Box alignSelf="end">
                                <Text size="small" color="brand">
                                    {videoStart} - {videoEnd} of{" "}
                                    {this.props.search.results.totalVideos}
                                </Text>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        fill
                        gap="none"
                        pad={{ left: "medium" }}
                        align="center"
                        justify="center"
                        alignSelf="center"
                        height={{ min: "small" }}
                    >
                        <Text
                            color="brand"
                            size="large"
                            weight="bold"
                            alignSelf="center"
                        >
                            No results found matching search criteria
                        </Text>
                    </Box>
                )}
            </Box>
        );
    };

    renderPagination = (size: string) => {
        let lastIndex = _.some(
            ["xsmall", "medium_small", "medium"],
            (value) => size === value
        )
            ? 2
            : 5;
        let totalPages = this.props.search.results.totalPages;
        let currentpage = this.props.search.request.currentPage;
        let pages: any = [];
        let maxStartPage = totalPages - (lastIndex - 1);
        let startPage = Math.min(currentpage, maxStartPage);

        for (
            var index = 0;
            index < lastIndex && startPage + index <= totalPages;
            index++
        ) {
            let page = startPage + index;

            pages.push(
                <PaginationItem key={`page-${page}`} active={currentpage === page} >
                    <PaginationLink page={page} onClick={(event) => { this.gotoPage(event); }} >
                        {page}
                    </PaginationLink>
                </PaginationItem>
            );
        }
        return this.props.search.results.totalPages > 2 ? (
            <Pagination
                size="sm"
                aria-label="Page navigation"
                style={{ margin: 0 }}
            >
                <PaginationItem disabled={currentpage === 1}>
                    <PaginationLink
                        first
                        page="first"
                        onClick={(event) => {
                            this.gotoPage(event);
                        }}
                    />
                </PaginationItem>
                <PaginationItem disabled={currentpage === 1}>
                    <PaginationLink
                        previous
                        page="previous"
                        onClick={(event) => {
                            this.gotoPage(event);
                        }}
                    />
                </PaginationItem>
                {pages}
                <PaginationItem disabled={currentpage === totalPages}>
                    <PaginationLink
                        next
                        page="next"
                        onClick={(event) => {
                            this.gotoPage(event);
                        }}
                    />
                </PaginationItem>
                <PaginationItem disabled={currentpage === totalPages}>
                    <PaginationLink
                        last
                        page="last"
                        onClick={(event) => {
                            this.gotoPage(event);
                        }}
                    />
                </PaginationItem>
            </Pagination>
        ) : null;
    };

    gotoPage = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        let totalPages = this.props.search.results.totalPages;
        let currentpage = this.props.search.request.currentPage;
        let nextPage = currentpage;
        let selectedPage =
            event.currentTarget.getAttribute("page") ?? undefined;

        switch (selectedPage) {
            case "first": {
                nextPage = 1;
                break;
            }
            case "next": {
                nextPage = Math.min(totalPages, currentpage + 1);
                break;
            }
            case "previous": {
                nextPage = Math.max(1, currentpage - 1);
                break;
            }
            case "last": {
                nextPage = totalPages;
                break;
            }
            default: {
                if (selectedPage) {
                    nextPage = parseInt(selectedPage);
                }
                break;
            }
        }
        if (this.props.search.request.currentPage !== nextPage) {
            this.props.setCurrentPage(nextPage);
            setTimeout(() => {
                this.performSearch();
            }, 0);
        }
    };

    toggleShowSearch = () => {
        this.setState({ showSearch: !this.state.showSearch });
    };

    onIncludeCaptionChange = (checked: boolean) => {
        this.props.setIncludeCaptions(checked);
        setTimeout(() => {
            this.performSearch();
        }, 0);
    };

  onLanguageChange = (lang: any) => {
        var location = { ...this.props.history.location };
        const currentQuery = queryString.parse(location.search);

        // Update lang
        if (lang.value === "ALL") {
            delete currentQuery.lang;
        } else {
            currentQuery.lang = lang.value;
        }

        // Update cc for Japanese
        if (lang.value === "ja-JP") {
            currentQuery.cc = "off";
        } else {
            delete currentQuery.cc;
        }

        const updatedSearch = queryString.stringify(currentQuery);

        var request = {
            ...this.props.search.request,
            language: lang,
            includeCaptions: lang.value === "ja-JP" ? false : this.props.search.request.includeCaptions,
        };

        // Update app state and push URL
        this.props.setLanguage(lang);
        this.props.history.push({
            pathname: location.pathname,
            search: updatedSearch ? `?${updatedSearch}` : '',
            state: request,
        });

        if (lang.value === "ja-JP") {
            this.props.setIncludeCaptions(false);
        }

        setTimeout(() => {
            this.performSearch();
        }, 100);

        // let search = lang.value === "ALL" ? undefined : `lang=${lang.value}`;
        // //var location = Object.assign({}, this.props.history.location);
        // //var request = Object.assign({}, this.props.search.request, { language: lang, includeCaptions: false });
        // let additionalParams = lang.value === "ja-JP" ? "&cc=off" : "";

        // this.props.setLanguage(lang);
        // this.props.history.push({
        //     pathname: location.pathname,
        //     search: search ? `${search}${additionalParams}` : 'lang=',
        //     state: request,
        // });
        // if (lang.value === "ja-JP") {
        //     this.props.setIncludeCaptions(false);
        // }
        // setTimeout(() => {
        //     this.performSearch();
        // }, 100);
    };

    

    onKeywordChange = (value: string) => {
        this.props.setKeyword(value);
        if (
            (value === "" || value.indexOf('"') !== -1) &&
            this.props.search.request.sortBy !== null &&
            this.props.search.request.sortBy.value === "Relevance"
        ) {
            this.props.setSortOrder(defaultSortOrder);
        }
    };

    onSorOrderChange = (value: any) => {
        this.props.setSortOrder(value);
        setTimeout(() => {
            this.performSearch();
        }, 0);
    };

    onSearchClicked = () => {
        this.props.setCurrentPage(1);
        setTimeout(() => {
            this.performSearch();
        }, 0);
    };

    categorySearch = (category: CategoryItem) => {
        let parentCategory: CategoryItem | undefined = category;

        if (this.props.search.request.category === category.url) {
            let categories: CategoryItem[] = JSON.parse(
                JSON.stringify(this.props.search.results.categories)
            );
            let items = category.url.split("/");
            let searchItems = categories;

            items.forEach((item) => {
                let found = searchItems.find((si) => {
                    return si.url === item;
                });
                if (found) {
                    parentCategory = found;
                    searchItems = found.children;
                }
            });
        }
        if (
            parentCategory.url === category.url &&
            this.props.search.request.category === category.url
        ) {
            this.setState({ scopeText: "" });
            this.props.changeCategory({
                scope: "ALL",
                category: "",
            });
        } else {
            this.setState({ scopeText: parentCategory.text });
            this.props.changeCategory({
                scope: "CATEGORY",
                category: parentCategory.url,
            });
        }
        this.props.setCurrentPage(1);
        setTimeout(() => {
            this.performSearch();
        }, 0);
    };

    onKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
            this.performSearch();
        }
    };

    performSearch = () => {
        this.setState({ showSearch: false, collapsed: true });

        this.props.performSearch();
    };

    gotoVideo = (video: VideoInfo) => {
        this.props.history.push(video.href, this.props.search.request);
    };

    public render() {
        return (
            <React.Fragment>
                <ResponsiveContext.Consumer>
                    {(size) => (
                        <Box fill align="center">
                            <Helmet>
                                <title>Videos | HPE Video Gallery</title>
                                <meta name="version" content={`${this.props.search.results.version}`} />
                                <meta name="description" content="Watch the latest Hewlett Packard Enterprise videos in our HPE video gallery." />
                                <meta name="robots" content="noindex" />
                            </Helmet>
                            {this.props.search.loading ? (
                                <Layer background="none">
                                    <Box align="center" justify="center" height="xxsmall" width="xsmall">
                                        <Image src="images/loader.svg" />
                                    </Box>
                                </Layer>
                            ) : (
                                <Box fill>
                                    <Box direction="row" fill="horizontal">
                                        <Heading level={3}>
                                            Video Gallery
                                        </Heading>
                                    </Box>
                                    {size === "small" || size === "xsmall" ? (
                                        <Box fill>
                                            {this.state.showSearch ? (
                                                <Box fill>
                                                    {this.renderSearch()}
                                                </Box>
                                            ) : (
                                                <Box fill>
                                                    {this.renderResults(size)}
                                                </Box>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box fill direction="row-responsive">
                                            <Box flex elevation="medium" width={{ max: "medium", min: "medium" }} >
                                                {this.renderSearch()}
                                            </Box>
                                            <Box flex fill>
                                                {this.renderResults(size)}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </ResponsiveContext.Consumer>
            </React.Fragment>
        );
    }
}

export default withRouter(connect(
    (state: ApplicationState) => state, // Selects which state properties are merged into the component's props
    mapDispatchToProps)(Search as any));
